import {
  Filters,
  GetSamplesInfiniteQueryParams,
  Sample,
  SampleStatusEnum,
  getSampleBusinessDisplayName,
  useGetOwnerNameQuery,
  useGetSamplesInfiniteQuery
} from 'prove-common'
import type { PlanStatusEnum } from 'prove-common'
import React from 'react'

const producerSearchDebounceMs = 300

const sampleMatchesProducerSearch = (sample: Sample, producerSearch: string) =>
  [
    getSampleBusinessDisplayName(sample),
    sample.externalReferenceId,
    sample.oversightObject,
    sample.orgnr?.toString()
  ].some((value) => value?.toLowerCase().includes(producerSearch) ?? false)

export const useSamples = ({
  filters,
  activePlan,
  isEnabled = true
}: {
  filters: Filters<SampleStatusEnum>
  activePlan: boolean | undefined
  isEnabled: boolean | undefined
}) => {
  const producerSearch = filters.producerSearch?.trim().toLowerCase()
  const [debouncedProducerSearch, setDebouncedProducerSearch] =
    React.useState(producerSearch)
  const cancelAutoFetchRef = React.useRef(false)
  const isAutoFetchingRef = React.useRef(false)

  React.useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => {
      setDebouncedProducerSearch(producerSearch)
    }, producerSearchDebounceMs)

    return () => globalThis.clearTimeout(timeoutId)
  }, [producerSearch])

  // Memoize stable, sorted params to avoid recreating query keys with different ordering
  const params: GetSamplesInfiniteQueryParams = React.useMemo(() => {
    const planStatuses: PlanStatusEnum[] | undefined = activePlan
      ? ['active']
      : undefined
    return {
      isEnabled,
      ownerScope: 'subtree',
      userId: filters.userId,
      ownerIds: filters.ownerId ? [String(filters.ownerId)] : undefined,
      productIds: (filters.productIds ?? [])
        .map(Number)
        .filter((n) => !Number.isNaN(n))
        .sort((a, b) => a - b),
      matrixIds: (filters.matrixIds ?? [])
        .map(Number)
        .filter((n) => !Number.isNaN(n))
        .sort((a, b) => a - b),
      sampntTypeIds: (filters.sampntTypeIds ?? [])
        .map(Number)
        .filter((n) => !Number.isNaN(n))
        .sort((a, b) => a - b),
      sampleIds: (filters.sampleIds ?? [])
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .sort((a, b) => a.localeCompare(b)),
      substanceIds: (filters.substanceIds ?? [])
        .map(Number)
        .filter((n) => !Number.isNaN(n))
        .sort((a, b) => a - b),
      status: [...(filters.status ?? [])].sort((a, b) => a.localeCompare(b)),
      result: filters?.result,
      planStatuses,
      fromDate: filters.fromDate || undefined,
      toDate: filters.toDate || undefined,
      sort: 'takenDate,desc'
    }
  }, [
    isEnabled,
    activePlan,
    filters.userId,
    filters.ownerId,
    filters.productIds,
    filters.matrixIds,
    filters.sampntTypeIds,
    filters.sampleIds,
    filters.substanceIds,
    filters.status,
    filters.result,
    filters.fromDate,
    filters.toDate
  ])

  const {
    data: samples,
    isPending: isPendingSamples,
    isSuccess: isSuccessSamples,
    isError: isErrorSamples,
    isFetching: isFetchingSamples,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetSamplesInfiniteQuery(params)

  const fetchUntilProducerSearchMatch = React.useCallback(async () => {
    if (
      debouncedProducerSearch == null ||
      debouncedProducerSearch.length === 0 ||
      !isEnabled ||
      isPendingSamples ||
      !hasNextPage
    ) {
      return
    }

    cancelAutoFetchRef.current = false
    isAutoFetchingRef.current = true

    try {
      let stillHasNext = Boolean(hasNextPage)
      while (stillHasNext && !cancelAutoFetchRef.current) {
        const result = await fetchNextPage()
        const hasMatch =
          result.data?.pages
            .flatMap((page) => page.content)
            .filter((sample): sample is Sample => sample != null)
            .some((sample) =>
              sampleMatchesProducerSearch(sample, debouncedProducerSearch)
            ) ?? false

        if (hasMatch) break

        stillHasNext = Boolean(result.hasNextPage)
      }
    } finally {
      isAutoFetchingRef.current = false
    }
  }, [
    debouncedProducerSearch,
    isEnabled,
    isPendingSamples,
    hasNextPage,
    fetchNextPage
  ])

  const {
    data: ownerName,
    isLoading: isLoadingOwnerName,
    isError: isErrorOwnerName
  } = useGetOwnerNameQuery(filters.ownerId)

  const filteredSamples: Sample[] = React.useMemo(() => {
    const allSamples =
      samples?.pages
        .flatMap((page) => page.content)
        .filter((sample): sample is Sample => sample != null) ?? []

    const uniqueSamples = Array.from(
      new Map(allSamples.map((sample) => [sample.id, sample] as const)).values()
    )

    if (
      debouncedProducerSearch == null ||
      debouncedProducerSearch.length === 0
    ) {
      return uniqueSamples
    }

    return uniqueSamples.filter((sample) =>
      sampleMatchesProducerSearch(sample, debouncedProducerSearch)
    )
  }, [debouncedProducerSearch, samples?.pages])

  React.useEffect(() => {
    if (
      debouncedProducerSearch == null ||
      debouncedProducerSearch.length === 0 ||
      !isEnabled ||
      !hasNextPage ||
      isPendingSamples ||
      isFetchingNextPage ||
      isAutoFetchingRef.current ||
      filteredSamples.length > 0
    ) {
      cancelAutoFetchRef.current = true
      return
    }

    fetchUntilProducerSearchMatch().catch(() => {
      // Query state already exposes fetch errors to consumers.
    })

    return () => {
      cancelAutoFetchRef.current = true
    }
  }, [
    debouncedProducerSearch,
    isEnabled,
    hasNextPage,
    isPendingSamples,
    isFetchingNextPage,
    filteredSamples.length,
    fetchUntilProducerSearchMatch
  ])

  const isProducerSearchResolving =
    debouncedProducerSearch != null &&
    debouncedProducerSearch.length > 0 &&
    filteredSamples.length === 0 &&
    (isPendingSamples || isFetchingNextPage || hasNextPage)

  const isPending =
    isPendingSamples || isLoadingOwnerName || isProducerSearchResolving
  const isError = isErrorSamples || isErrorOwnerName
  const isFetching = isFetchingSamples || isLoadingOwnerName

  const totalElements =
    debouncedProducerSearch != null && debouncedProducerSearch.length > 0
      ? filteredSamples.length
      : samples?.pages[0].totalElements

  return {
    ownerName,
    filteredSamples,
    totalElements,
    isPending,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    hasNextPage,
    isSuccessSamples
  }
}
